<template>
  <article
    class="card-action"
    :class="{ 'card-action--visible': isVisible }"
    :style="cardStyle"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="card-action__icon-wrap">
      <i :class="['card-action__icon', icon]" aria-hidden="true"></i>
    </div>
    <div class="card-action__content">
      <h3 class="card-action__title">{{ title }}</h3>
      <p class="card-action__description">{{ description }}</p>
    </div>
    <span class="card-action__arrow" aria-hidden="true">
      <i class="fas fa-arrow-right"></i>
    </span>
  </article>
</template>

<script>
export default {
  name: "CardAction",

  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    route: {
      type: String,
      required: true,
    },
    gradientStart: {
      type: String,
      default: "#05A85A",
    },
    gradientEnd: {
      type: String,
      default: "#8FC74B",
    },
    delay: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      isVisible: false,
    };
  },

  computed: {
    cardStyle() {
      return {
        "--gradient-start": this.gradientStart,
        "--gradient-end": this.gradientEnd,
        "--animation-delay": `${this.delay}ms`,
      };
    },
  },

  mounted() {
    requestAnimationFrame(() => {
      this.isVisible = true;
    });
  },

  methods: {
    handleClick() {
      this.$emit("navigate", this.route);
    },
  },
};
</script>

<style scoped lang="scss">
.card-action {
  --gradient-start: #05a85a;
  --gradient-end: #8fc74b;
  --animation-delay: 0ms;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  min-height: 180px;
  border-radius: 1.25rem;
  background: linear-gradient(135deg, #ffffff 0%, #e3f1e9 100%);
  border: 1px solid rgba(5, 168, 90, 0.12);
  box-shadow: 0 4px 20px rgba(5, 168, 90, 0.08);
  cursor: pointer;
  overflow: hidden;
  opacity: 0;
  transform: translateY(24px) scale(0.98);
  transition:
    opacity 0.5s ease var(--animation-delay),
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) var(--animation-delay),
    box-shadow 0.3s ease,
    border-color 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end));
    opacity: 0;
    transition: opacity 0.35s ease;
    z-index: 0;
  }

  &--visible {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  &:hover,
  &:focus {
    outline: none;
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 16px 40px rgba(5, 168, 90, 0.22);
    border-color: transparent;

    &::before {
      opacity: 1;
    }

    .card-action__icon-wrap {
      background: rgba(255, 255, 255, 0.25);
      transform: scale(1.08);
    }

    .card-action__icon,
    .card-action__title,
    .card-action__description,
    .card-action__arrow {
      color: #ffffff;
    }

    .card-action__arrow {
      transform: translateX(4px);
      opacity: 1;
    }
  }

  &:focus-visible {
    box-shadow:
      0 0 0 3px #ffffff,
      0 0 0 6px var(--gradient-start);
  }

  &__icon-wrap,
  &__content,
  &__arrow {
    position: relative;
    z-index: 1;
  }

  &__icon-wrap {
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end));
    box-shadow: 0 6px 16px rgba(5, 168, 90, 0.25);
    transition: transform 0.3s ease, background 0.3s ease;
  }

  &__icon {
    font-size: 1.35rem;
    color: #ffffff;
    transition: color 0.3s ease;
  }

  &__title {
    margin: 0 0 0.35rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a3d2e;
    transition: color 0.3s ease;
  }

  &__description {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.45;
    color: #4a6b5c;
    transition: color 0.3s ease;
  }

  &__arrow {
    margin-top: auto;
    align-self: flex-end;
    font-size: 0.9rem;
    color: var(--gradient-start);
    opacity: 0.7;
    transition: transform 0.3s ease, color 0.3s ease, opacity 0.3s ease;
  }
}
</style>
